import * as React from 'react';
import { createUseStyles } from 'react-jss';

import { Product } from '../interfaces';
import { useAppData, useIntl } from '../providers';
import { ContainerTypes } from './index';

interface StyleProps {
    smallFont: boolean;
}

const useStyles = createUseStyles({
    characteristics: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        position: 'relative',
        alignContent: 'flex-start',
        margin: '1rem -1rem',
        fontSize: ({ smallFont }: StyleProps) => (smallFont ? '.8rem' : '1rem'),
        '& ul': {
            listStyleType: 'none',
            marginLeft: 0,
            marginTop: '.1rem',
            '& li': {
                margin: '.1rem 0',
                paddingLeft: '15px',
                '&:before': {
                    width: '7px',
                    height: '7px',
                },
            },
        },
        '& > *': {
            flex: 1,
            margin: '0 1rem',
        },
    },
    productId: {
        flex: '1 1 100%',
        marginBottom: '1rem',
    },
    group: {
        '& + &': {
            marginTop: '1rem',
        },
        '& p': {
            marginTop: '.1rem',
        },
    },
    containerTypeTable: {
        width: '100%',
        maxWidth: '250px',
        '& td': {
            background: 'transparent',
            border: 0,
            padding: 0,
            '&:last-child': {
                textAlign: 'right',
            },
        },
    },
    priceHint: {
        fontSize: '0.8em',
        maxWidth: '250px',
    },
});

interface ProductCharacteristicsProps {
    product: Product;
    smallFont?: boolean;
    allowPreview?: boolean;
}

const ProductCharacteristics: React.FC<ProductCharacteristicsProps> = ({
    product,
    smallFont = true,
    allowPreview = false,
}: ProductCharacteristicsProps) => {
    const classes = useStyles({ smallFont });
    const { translate } = useIntl();
    const { isShop } = useAppData();

    return (
        <div className={classes.characteristics}>
            <div className={classes.productId}>
                <strong>{product.id}</strong>
            </div>
            <div>
                <div className={classes.group}>
                    <strong>{translate('productCard.header.attributes', 'Attributes')}</strong>
                    {product.attributes.length > 0 ? (
                        <ul>
                            {product.attributes.map((attribute) => (
                                <li key={attribute.identifier}>{attribute.label}</li>
                            ))}
                        </ul>
                    ) : (
                        <p>{translate('productCard.status.notAvailable', 'not available')}</p>
                    )}
                </div>

                {product.ingredients && (
                    <div className={classes.group}>
                        <strong>{translate('productCard.header.ingredients', 'Ingredients')}</strong>
                        <p dangerouslySetInnerHTML={{ __html: product.ingredients }} />
                    </div>
                )}
            </div>
            {isShop && (
                <div className={classes.group}>
                    <strong>{translate('productCard.header.containerTypes', 'Container types')}</strong>
                    {product.containerTypes.length > 0 ? (
                        <ContainerTypes containerTypes={product.containerTypes} allowPreview={allowPreview} alcTaxIncluded={product?.alcTaxIncluded} />
                    ) : (
                        <p>{translate('productCard.status.notAvailable', 'not available')}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default React.memo(ProductCharacteristics);
